import React, { useRef, useState, useEffect } from "react";
import {
  Row,
  Col,
  Table,
  Spin,
  Select,
  DatePicker,
  Space,
  Form,
  message,
} from "antd";
import { Pie } from "@ant-design/plots";
import Button from "atoms/Button";
import moment from "moment";
import styled from "@emotion/styled";
import { CSVLink } from "react-csv";
import { connect } from "react-redux";
import {
  getUserRoleId,
  getSelectedDepartmentId,
  getAllDepartmentList,
  getLeaveCategoryList,
  getShiftTimeMaster,
  getClientid,
  getUserInfo,
} from "redux/selectors";
import { bindActionCreators } from "redux";
import { toggleProcessingModal } from "redux/actions";

import { CALL_API } from "common/API";
import {
  MONTH_LIST,
  REPORT_DURATION_TYPES,
  STATUS_CODE,
} from "common/Constants";

import {
  addDaysinDate,
  dateInDetail,
  getKenyanDateTime,
  getMonthDateRange,
  getMySqlDate,
  momentDate,
  addMonthinDate,
} from "utils/Date";
import COLORS from "common/Colors";
import { includes, reduce, concat, find, get, filter, orderBy } from "lodash";
import ViewMore from "organisms/ViewMore";
import { ButtonOutlined } from "reusableComponent/UIButtons";
import { useTranslation } from "react-i18next";
import download from "downloadjs";
import ViewDocument from "reusableComponent/Popup/viewDocument";

const StyledTable = styled(Table)`
  padding-top: 20px;
  .ant-table-content {
    overflow-x: auto;
    white-space: nowrap;
  }
  table {
    width: 100% !important;
  }
  .ant-table-cell {
    padding: 20px;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: bold;
    color: ${COLORS.SECONDARY_BLACK};
  }
  .ant-table-cell:nth-last-child(2) {
    min-width: 200px;
    white-space: normal;
  }
  .non-white {
    background: #f9f9f9;
  }
`;

const StyledSearchForm = styled(Form)`
  .ant-form-item-with-help {
    margin-bottom: 0px;
    .ant-form-item-explain-connected {
      display: none;
    }
  }
  .ant-form-item {
    display: inline-block;
  }
  .outer-form {
    margin-right: 0px;
  }
  .ant-select-selector {
    max-width: 180px;
    min-width: 180px;
    margin-bottom: 10px;
    width: 180px;
  }
`;

const StyledButton = styled(Button)`
  width: 120px;
  border: 1px solid ${COLORS.PRIMARY};
  color: ${COLORS.PRIMARY};
  width: initial;
  background: transparent;
  border-radius: 5px;
`;

const StyledChartCol = styled(Col)`
  width: 100%;
  padding: 40px 10px 10px 10px;
`;

const StyledButtonoutlined = styled(ButtonOutlined)`
  @media (max-width: 900px) {
    margin-left: -20px !important;
  }
  @media (max-width: 768px) {
    margin-left: -20px !important;
    margin-top: -1px !important;
  }
  @media (max-width: 575px) {
    margin-left: 0 !important;
    position: absolute;
    left: 0;
    margin-top: -1px;
  }
`;

function LeaveReport({
  userRoleId,
  leaveCategoryList,
  allDepartmentList,
  selectedDepartmentId,
  ClientId,
  userInfo,
}) {
  const { t, i18n } = useTranslation();

  const [leaveList, setLeaveList] = useState([]);
  const [staffLeaveList, setStaffLeaveList] = useState([]);
  const [staffName, setStaffName] = useState(" ");
  const [CSVLeaveList, setCSVLeaveList] = useState([]);
  const [displayTableLoader, setDisplayTableLoader] = useState(false);
  const [approvedLeaves, setApprovedLeaves] = useState(0);
  const [pendingLeaves, setPendingLeaves] = useState(0);
  const [form] = Form.useForm();
  const csvLinkEl = useRef();
  const [filterList, setFilterList] = useState([]);
  const [expandedReasons, setExpandedReasons] = useState("");
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [previewURL, setPreviewURL] = useState();

  const STAFF_LIST_ALL = leaveList.map((val) => ({
    value: val.userid,
    label: val.name,
  }));

  for (let i = 0; i < STAFF_LIST_ALL.length; i++) {
    filterList.push(STAFF_LIST_ALL[i]);
  }

  const STAFF_LIST = [...new Set(filterList.map((a) => JSON.stringify(a)))].map(
    (a) => JSON.parse(a)
  );

  const ALL = [{ value: "*", label: t("All Employees") }];

  const CSVHeaders = [
    { label: t("Employee ID"), key: "user_id" },
    { label: t("Name"), key: "name" },
    { label: t("Department"), key: "job_title_name" },
    { label: t("Start Date"), key: "start_date" },
    { label: t("End Date"), key: "end_date" },
    { label: t("Days"), key: "days" },
    { label: t("Leave Type"), key: "leave_category_name" },
    { label: t("Status"), key: "status" },
  ];

  const handlePreviewClick = async (data) => {
    try {
      const preview = data?.fileName?.split(".")?.pop();
      setLoading(true);

      const { code, url } = await CALL_API(
        `document-list/${ClientId}`,
        "post",
        {
          user_leave_id: data?.user_leave_id,
          method: "file",
          userId: data?.userid,
        }
      );

      if (code === STATUS_CODE.SUCCESS) {
        if (["jpg", "jpeg", "png", "svg", "pdf"].includes(preview)) {
          setPreviewURL(url);
          setPreview(true);
        } else {
          download(url);
          setPreview(false);
        }
      } else {
        message.error("Preview failed. Try again.");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: t("Employee ID"),
      dataIndex: "employeeid",
      key: "employeeid",
      width: "8%",
    },
    {
      title: t("Name"),
      dataIndex: "name",
      key: "name",
      width: "12%",
    },
    {
      title: t("Department"),
      dataIndex: "department_name",
      key: "department_name",
      width: "10%",
    },
    {
      title: t("Start Date"),
      dataIndex: "start_date",
      key: "start_date",
      width: "7%",
      sorter: (a, b) => (
        moment(a.start_date).format("DD"),
        moment(a.start_date).format("MM"),
        moment(a.start_date).format("YYYY") - moment(b.start_date).format("DD"),
        moment(b.start_date).format("MM"),
        moment(b.start_date).format("YYYY")
      ),
      render: (_, record) => {
        // Amended leaves keep their original start_date until the amendment is
        // fully (Level 2) approved - while an amendment is pending, the proposed
        // date lives on new_start_date, matching the pattern used in
        // Approval/Myleavereport.
        const date =
          record.new_start_date === "null" ||
          record.new_start_date === "" ||
          record.new_start_date === null
            ? record.start_date
            : record.new_start_date;
        return moment(date).format("DD MMM YYYY");
      },
    },
    {
      title: t("Start Day"),
      dataIndex: "start_date",
      key: "start_date",
      width: "7%",
      render: (_, record) => {
        const date =
          record.new_start_date === "null" ||
          record.new_start_date === "" ||
          record.new_start_date === null
            ? record.start_date
            : record.new_start_date;
        return moment(date).format("dddd");
      },
    },
    {
      title: t("End Date"),
      dataIndex: "end_date",
      key: "end_date",
      width: "7%",
      render: (_, record) => {
        const date =
          record.new_end_date === "null" ||
          record.new_end_date === "" ||
          record.new_end_date === null
            ? record.end_date
            : record.new_end_date;
        return moment(date).format("DD MMM YYYY");
      },
    },
    {
      title: t("End Day"),
      dataIndex: "end_date",
      key: "end_date",
      width: "10%",
      render: (_, record) => {
        const date =
          record.new_end_date === "null" ||
          record.new_end_date === "" ||
          record.new_end_date === null
            ? record.end_date
            : record.new_end_date;
        return moment(date).format("dddd");
      },
    },
    {
      title: t("Days"),
      dataIndex: "number_of_days",
      key: "number_of_days",
      width: "7%",
      render: (_, record) => {
        if (
          record.new_number_of_days === null ||
          record.new_number_of_days === "" ||
          record.new_number_of_days === "null"
        ) {
          return record.number_of_days;
        } else {
          return record.new_number_of_days;
        }
      },
    },
    {
      title: t("Leave Type"),
      dataIndex: "leave_category_name",
      key: "leave_category_name",
      width: "10%",
    },
    {
      title: t("Attachment"),
      dataIndex: "fileName",
      key: "fileName",
      width: 150,
      render: (text, record) =>
        text ? (
          <a
            href="#"
            style={{ color: "#2d53da" }}
            className="text-wrap"
            onClick={(e) => {
              e.stopPropagation();
              handlePreviewClick(record);
            }}
          >
            {text}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: t("Reliever Name"),
      dataIndex: "relievername",
      key: "relievername",
      width: "10%",
      render: (text) => {
        return text ? text : "-";
      },
    },
    {
      title: t("Amended Leave"),
      dataIndex: "having_cancel_request",
      key: "having_cancel_request",
      width: "8%",
      render: (having_cancel_request) =>
        having_cancel_request === "1" || having_cancel_request === 1
          ? t("Yes")
          : t("No"),
    },
    {
      title: t("Level 1 Approved by"),
      dataIndex: "approved_rejected_byName",
      key: "approved_rejected_byName",
      width: "10%",
      render: (_, record) => {
        const isAmended =
          record?.having_cancel_request === "1" ||
          record?.having_cancel_request === 1;

        // Amended leaves track their own Level 1 approval separately so the
        // original leave's approval isn't shown/overwritten.
        const approverName = isAmended
          ? record?.new_approved_rejected_byName
          : record?.approved_rejected_byName;

        return approverName ? approverName : "-";
      },
    },
    {
      title: t("Level 2 Approved by"),
      dataIndex: "approved_rejected_by_higherofficials",
      key: "approved_rejected_by_higherofficials",
      width: "10%",
      render: (_, record) => {
        const isAmended =
          record?.having_cancel_request === "1" ||
          record?.having_cancel_request === 1;

        // Amended leaves track their own Level 2 approval separately so the
        // original leave's approval isn't shown/overwritten.
        const approverName = isAmended
          ? record?.new_approved_rejected_by_higherofficials
          : record?.approved_rejected_by_higherofficials;

        return approverName ? approverName : "-";
      },
    },
    {
      title: t("Reason"),
      dataIndex: "reason",
      key: "reason",
      width: "15%",
      render: (date, record) => {
        return (
          <>
            <ViewMore value={`${record.reason}${record.decline_reason}`} />
          </>
        );
      },
    },
    {
      title: t("Status"),
      dataIndex: "status_name",
      key: "status_name",
      align: "center",
    },
  ];

  const data = [
    { type: t("Approved"), value: approvedLeaves },
    { type: t("Pending"), value: pendingLeaves },
  ];

  const config = {
    width: 150,
    height: 150,
    appendPadding: 6,
    data,
    angleField: "value",
    colorField: "type",
    color: [COLORS.CHART.AVAILABLE, COLORS.CHART.CONSUMED],
    radius: 1,
    innerRadius: 0.8,
    legend: { position: "top" },
    statistic: {
      title: false,
      content: {
        style: {
          whiteSpace: "pre-wrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontSize: "14px",
        },
        content: `${approvedLeaves}/${pendingLeaves}`,
      },
    },
  };

  useEffect(() => {
    getLeaveList();
  }, [
    selectedDepartmentId,
    userRoleId,
    form.getFieldValue("duration_type"),
    form.getFieldValue("start_date"),
    form.getFieldValue("end_date"),
    form.getFieldValue("month"),
    form.getFieldValue("department"),
    form.getFieldValue("leave_type"),
  ]);

  useEffect(() => {
    form.resetFields();
  }, [i18n.language]);

  const getLeaveList = async () => {
    setLeaveList([]);
    const {
      start_date,
      end_date,
      duration_type,
      month,
      department,
      leave_type,
    } = form.getFieldsValue();
    form.setFieldsValue({ staff_name: t("All Employees") });
    setDisplayTableLoader(true);
    const { startDateOfMonth, lastDateOfMonth } = getMonthDateRange(
      dateInDetail(getKenyanDateTime()).year,
      get(find(MONTH_LIST, { value: month }), "index", 0)
    );
    const { code, leaves = [] } = await CALL_API(
      `leave-report/${ClientId}`,
      "post",
      {
        department_id: [""],
        leave_type,
        status_id: "4,5",
        start_date: getMySqlDate(
          duration_type === 2 ? startDateOfMonth : start_date
        ),
        end_date: getMySqlDate(
          duration_type === 2 ? lastDateOfMonth : end_date
        ),
        reportingmanager: userInfo?.userid,
      }
    );
    setDisplayTableLoader(false);
    if (includes([STATUS_CODE.SUCCESS, STATUS_CODE.RECORD_NOT_FOUND], code)) {
      const leaveData = orderBy(leaves, ["start_date"], ["desc"]);
      setLeaveList(leaveData?.reverse());
      setStaffLeaveList(leaveData.reverse());
      setPendingLeaves(filter(leaveData, { status_id: "4" }).length);
      setApprovedLeaves(filter(leaveData, { status_id: "5" }).length);
    }
  };

  const validateDateRange = (date) => {
    const endDate = moment(form.getFieldValue("end_date"));
    if (endDate < date) {
      form.setFieldsValue({ end_date: date });
    }
  };

  const prepareCSV = async (event, done) => {
    if (leaveList.length > 0) {
      setCSVLeaveList(
        reduce(
          leaveList?.reverse(),
          function (result, leave) {
            const isAmended =
              leave?.having_cancel_request === "1" ||
              leave?.having_cancel_request === 1;
            const csvStartDate =
              leave.new_start_date === "null" ||
              leave.new_start_date === "" ||
              leave.new_start_date === null
                ? leave.start_date
                : leave.new_start_date;
            const csvEndDate =
              leave.new_end_date === "null" ||
              leave.new_end_date === "" ||
              leave.new_end_date === null
                ? leave.end_date
                : leave.new_end_date;
            const csvDays =
              leave.new_number_of_days === "null" ||
              leave.new_number_of_days === "" ||
              leave.new_number_of_days === null
                ? leave.number_of_days
                : leave.new_number_of_days;
            const {
              date_th: end_day,
              shortMonthName: end_shortMonthName,
              year: end_year,
            } = dateInDetail(csvEndDate);
            const {
              date_th: start_day,
              shortMonthName: start_shortMonthName,
              year: start_year,
            } = dateInDetail(csvStartDate);
            result.push({
              "Employee ID": leave.employeeid,
              Name: leave.name,
              Department: leave.department_name,
              "Start Date": `${start_day} ${start_shortMonthName} ${start_year}`,
              "Start Day": `${moment(csvStartDate).format("dddd")}`,
              "End Date": `${end_day} ${end_shortMonthName} ${end_year}`,
              "End Day": `${moment(csvEndDate).format("dddd")}`,
              Days: csvDays,
              "Leave Type": leave.leave_category_name,
              "Amended Leave": isAmended ? t("Yes") : t("No"),
              "Level 1 Approved by":
                (isAmended
                  ? leave.new_approved_rejected_byName
                  : leave.approved_rejected_byName) || "-",
              "Level 2 Approved by":
                (isAmended
                  ? leave.new_approved_rejected_by_higherofficials
                  : leave.approved_rejected_by_higherofficials) || "-",
              Reason: leave.reason,
              Status: leave.status_name,
              "Reliever Name": leave?.relievername || "-",
            });
            return result;
          },
          []
        )
      );

      setTimeout(() => {
        csvLinkEl.current.link.click();
      });
    } else {
      message.error(t("No data available to download."));
    }
  };

  const handleChange = (event) => {
    if (event == "*") {
      getLeaveList();
    } else {
      setStaffName(event);
      const filteredStaffList = staffLeaveList.filter(
        (val) => val.userid === event
      );
      setLeaveList(filteredStaffList);
      setStaffLeaveList(staffLeaveList);
    }
  };

  const paginationOptions = {
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} ${t("of")} ${total} ${t("items")}`,
    pageSize: 100,
    showSizeChanger: false,
  };

  return (
    <>
      <Row className="page-title">
        <Col
          xs={24}
          sm={19}
          md={19}
          className="page-title-head"
          ref={containerRef}
        >
          <Space>
            <StyledSearchForm
              form={form}
              name="approval_form"
              layout="inline"
              initialValues={{
                duration_type: 2,
                month: dateInDetail(getKenyanDateTime()).monthName,
                start_date: momentDate(addMonthinDate(getKenyanDateTime(), -1)),
                end_date: momentDate(addDaysinDate(getKenyanDateTime())),
                department: "",
                leave_type: "",
              }}
              onFinish={getLeaveList}
            >
              <Form.Item
                name="duration_type"
                rules={[{ required: true, message: "" }]}
              >
                <Select
                  placeholder={t("Select...")}
                  options={REPORT_DURATION_TYPES.map((item) => ({
                    ...item,
                    label: t(item.label),
                  }))}
                  getPopupContainer={() => containerRef.current}
                />
              </Form.Item>

              <Form.Item className="outer-form" shouldUpdate>
                {({ getFieldValue }) => {
                  const duration_type = getFieldValue("duration_type");
                  return duration_type === 1 ? (
                    <>
                      <Form.Item
                        name="start_date"
                        rules={[
                          () => ({
                            validator(rule, value) {
                              const date = moment(value);
                              return date.isValid()
                                ? Promise.resolve()
                                : Promise.reject();
                            },
                          }),
                        ]}
                      >
                        <DatePicker
                          placeholder={t("Start date")}
                          format="DD-MM-YYYY"
                          onChange={validateDateRange}
                          allowClear={false}
                          getPopupContainer={() => containerRef.current}
                        />
                      </Form.Item>

                      <Form.Item
                        name="end_date"
                        rules={[
                          () => ({
                            validator(rule, value) {
                              const date = moment(value);
                              return date.isValid()
                                ? Promise.resolve()
                                : Promise.reject();
                            },
                          }),
                        ]}
                      >
                        <DatePicker
                          placeholder={t("End date")}
                          format="DD-MM-YYYY"
                          onChange={validateDateRange}
                          allowClear={false}
                          getPopupContainer={() => containerRef.current}
                        />
                      </Form.Item>
                    </>
                  ) : (
                    <Form.Item
                      name="month"
                      rules={[{ required: true, message: "" }]}
                    >
                      <Select
                        placeholder={t("Select...")}
                        options={MONTH_LIST}
                        getPopupContainer={() => containerRef.current}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>

              {(userRoleId === 1 || userRoleId === 6) && (
                <Form.Item name="department">
                  <Select
                    placeholder={t("Select Department")}
                    options={concat(
                      {
                        department_id: "",
                        department_name: t("All Departments"),
                      },
                      allDepartmentList
                    )}
                    fieldNames={{
                      label: "department_name",
                      value: "department_id",
                    }}
                    getPopupContainer={() => containerRef.current}
                  />
                </Form.Item>
              )}

              <Form.Item name="leave_type">
                <Select
                  showSearch
                  placeholder={t("Search leave type")}
                  name="leave_type"
                  optionFilterProp="leave_category_name"
                  filterOption={(input, option) =>
                    (option?.leave_category_name ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  filterSort={(optionA, optionB) => {
                    optionA.leave_category_name
                      .toLowerCase()
                      .localeCompare(optionB.leave_category_name.toLowerCase());
                  }}
                  options={concat(
                    {
                      leave_category_id: "",
                      leave_category_name: t("All Categories"),
                    },
                    leaveCategoryList
                  )}
                  fieldNames={{
                    label: "leave_category_name",
                    value: "leave_category_id",
                  }}
                  getPopupContainer={() => containerRef.current}
                />
              </Form.Item>

              <Form.Item name="staff_name">
                <Select
                  showSearch
                  optionFilterProp="children"
                  name="staff_name"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? "")
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? "").toLowerCase())
                  }
                  placeholder={t("Select Employee")}
                  options={concat(ALL, STAFF_LIST)}
                  value={staffName}
                  onChange={handleChange}
                  getPopupContainer={() => containerRef.current}
                />
              </Form.Item>
            </StyledSearchForm>
          </Space>
        </Col>

        <Col xs={24} sm={4} md={4} className="align-right">
          <StyledButtonoutlined
            onClick={prepareCSV}
            disabled={leaveList.length === 0}
          >
            {t("Export CSV file")}
          </StyledButtonoutlined>
          <CSVLink
            header={CSVHeaders}
            data={CSVLeaveList}
            filename={"leave-report.csv"}
            asyncOnClick={true}
            ref={csvLinkEl}
          />
        </Col>
      </Row>
      <br />
      {/* {approvedLeaves > 0 && pendingLeaves > 0 && (
        <Row>
          <StyledChartCol>
            <Pie {...config} />
          </StyledChartCol>
        </Row>
      )} */}
      <Spin spinning={displayTableLoader || loading}>
        <StyledTable
          dataSource={leaveList}
          columns={columns}
          rowClassName={(record, index) => (index % 2 === 0 ? "" : "non-white")}
          rowKey="user_leave_id"
          pagination={leaveList.length > 100 ? paginationOptions : false}
        />
      </Spin>
      <ViewDocument
        open={preview}
        onCancel={() => {
          setPreview(false);
          setPreviewURL("");
        }}
        data={previewURL}
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  userRoleId: getUserRoleId(state),
  selectedDepartmentId: getSelectedDepartmentId(state),
  allDepartmentList: getAllDepartmentList(state),
  leaveCategoryList: getLeaveCategoryList(state),
  shiftTimeMaster: getShiftTimeMaster(state),
  ClientId: getClientid(state),
  userInfo: getUserInfo(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    { updateProcessingModal: toggleProcessingModal },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(LeaveReport);
